'use client';
// Compact questionnaire experience (questionnaire_design='compact') — used by BOTH
// CategoryQuestionnaire and ProSmartIntake when the admin setting is flipped.
//
// Instead of the classic collapse into a dead "scroll back up" line (StickyQuizBar),
// the questionnaire CONDENSES on scroll into a compact bar that DOCKS AT THE TOP OF THE
// VIEWPORT, visually replacing the site's own Header (Header.tsx is `position: sticky;
// top: 0; z-index: 50` — this bar sits at `top: 0` too with a higher z-index, so it
// covers the header rather than pinning below it) — current question + horizontally-
// scrollable answer chips + progress dots, always answerable in place. Styled in the same
// blue-forward brand gradient as the header itself so it reads as "the header became the
// questionnaire," not an unrelated overlay. A companion bar pinned to the viewport bottom
// (CompactWaBar) shows the LIVE WhatsApp message forming from the answers.
//
// One QuestionnaireWizard instance is kept mounted throughout; only container styles
// and the wizard's `condensed` prop change on scroll, so answers/step state survive
// the transition in both directions.

import { useState, useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import type { Questionnaire } from './QuestionnaireWizard';
import { SMART_INTAKE_KEY } from './SmartIntakeBar';
import CompactWaBar, { type CompactBarPro } from './CompactWaBar';
import { WA_TYPING_PREVIEW_ENABLED } from '@/lib/featureFlags';
import WaIcon from './WaIcon';
import { useSmartIntakeCtx, notifySmartIntake, pageLoadMayWrite } from '@/lib/smartIntakeStore';
import { useTypewriter } from '@/lib/useTypewriter';
import { buildWaMessageSegments, proFirstName } from '@/lib/waMessage';

const QuestionnaireWizard = dynamic(() => import('./QuestionnaireWizard'), {
  ssr: false,
  loading: () => <WizardSkeleton />,
});
const WaSmartButton = dynamic(() => import('./WaSmartButton'), { ssr: false });

function WizardSkeleton() {
  return (
    <div style={{ padding: 18 }} aria-hidden>
      <div className="flex gap-2 flex-wrap">
        {[0, 1, 2, 3, 4].map(i => (
          <div key={i} className="rounded-full animate-pulse" style={{ width: 84, height: 34, background: '#eef2f7' }} />
        ))}
      </div>
    </div>
  );
}

const HEADER_OFFSET = 58; // Header.tsx's own height — used for scrollMarginTop (anchor scroll clearance) while NOT condensed, since the real header is still visible then
const BRAND_GRADIENT = 'linear-gradient(105deg, #5aa12f 0%, #4a8228 43%, #15589f 57%, #0d47a1 100%)';

// Live WA-message preview mounted INSIDE the card layout itself (expanded state only —
// condensed is already tight on space and CompactWaBar covers it at the bottom). Same
// typewriter reveal as CompactWaBar's pinned bottom bar and WaSmartButton's own bubble —
// the "act of writing" should read identically wherever the message shows up.
// 2026-07-29 (lib/featureFlags.ts): live-typing preview disabled site-wide — thin gate
// wrapper so the inner component's hooks are never conditionally skipped. Code preserved.
function InlineWaPreview(props: { categoryName: string; pro?: CompactBarPro; previewName?: string }) {
  if (!WA_TYPING_PREVIEW_ENABLED) return null;
  return <InlineWaPreviewInner {...props} />;
}

function InlineWaPreviewInner({ categoryName, pro, previewName }: {
  categoryName: string; pro?: CompactBarPro; previewName?: string;
}) {
  const ctx = useSmartIntakeCtx();
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);
  const displayName = pro ? proFirstName(pro.name, pro.firstName) : (previewName || 'המומחה');
  const segments = buildWaMessageSegments(displayName, mounted ? ctx : null, categoryName);
  const previewText = segments.filter((s): s is { text: string } => 'text' in s).map(s => s.text).join('');
  const hasPending = segments.some(s => 'pending' in s);
  const { typed, isTyping } = useTypewriter(previewText);

  return (
    <div style={{ borderTop: '1px solid #f1f5f9', padding: '12px 14px' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 5 }}>💬 ההודעה שלכם נכתבת:</div>
      <div style={{
        background: '#dcf8c6', borderRadius: '10px 2px 10px 10px', border: '1px solid #c7ecb0',
        padding: '8px 12px', fontSize: 13, lineHeight: 1.6, color: '#111', whiteSpace: 'pre-wrap',
      }}>
        {typed}
        {isTyping && <span className="iwp-caret" />}
        {hasPending && !isTyping && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, marginInlineStart: 4, verticalAlign: 'middle' }}>
            <span className="iwp-dot" /><span className="iwp-dot" /><span className="iwp-dot" />
          </span>
        )}
      </div>
      <style>{`
        .iwp-caret {
          display: inline-block; width: 3px; height: 1.05em; border-radius: 2px;
          background: #34b7f1; vertical-align: text-bottom; margin-inline-start: 2px;
          animation: iwpCaret 1.1s ease-in-out infinite;
        }
        @keyframes iwpCaret { 0%,100% { opacity: .15 } 50% { opacity: 1 } }
        .iwp-dot {
          display: inline-block; width: 5px; height: 5px; border-radius: 50%;
          background: #667781; animation: iwpBounce 1.2s ease-in-out infinite;
        }
        .iwp-dot:nth-child(2) { animation-delay: .15s; }
        .iwp-dot:nth-child(3) { animation-delay: .3s; }
        @keyframes iwpBounce { 0%,60%,100% { transform: translateY(0); opacity: .5; } 30% { transform: translateY(-3px); opacity: 1; } }
      `}</style>
    </div>
  );
}

interface Props {
  categorySlug: string;
  categoryName: string;
  // Category×city landing pages — city question pre-answered from the URL (see
  // CategoryQuestionnaire for the seeding contract this replicates).
  presetCity?: { name: string; lat: number; lng: number };
  initialQuestionnaire?: Questionnaire | null;
  // Pro pages: the page's pro — shown in the bottom bar (avatar/name/rating) and
  // targeted by its send button (ContactModal flow).
  pro?: CompactBarPro & { city?: string | null };
  // Category pages: name used in the bottom-bar live preview text.
  previewName?: string;
  // Article embed (#QUESTIONNAIRE block): skip the gradient banner entirely (both the
  // full and condensed-strip variants) — the article's own H2/prose already carries
  // that context, a repeated banner inside a small embedded card is redundant. See
  // docs/questionnaire-designs.md.
  hideHeader?: boolean;
  // Per-article hook question (2026-07-23): the category/service is location-independent
  // for THIS article (or in general — he_categories.location_independent), so the city
  // question is dropped entirely instead of asked. See lib/articleData.ts getArticleHook.
  skipCityQuestion?: boolean;
  // Article embed (Founder 17/10/26: no floating questionnaires in articles, site-wide): never condense/dock
  // to the top on scroll and no CompactWaBar pinned to the viewport bottom — the card stays in place in the article.
  noFloat?: boolean;
}

export default function CompactIntake({ categorySlug, categoryName, presetCity, initialQuestionnaire, pro, previewName, hideHeader, skipCityQuestion, noFloat }: Props) {
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null | undefined>(initialQuestionnaire);
  const [started, setStarted] = useState(false);
  const [condensed, setCondensed] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const fullHeight = useRef<number>(0);

  // Same localStorage seeding contract as the classic wrappers: city landing pages seed
  // the preset city immediately; otherwise clear ONLY a different-category leftover.
  useEffect(() => {
    try {
      if (presetCity && !pageLoadMayWrite(presetCity.name)) { /* a saved active choice is the page city (Back/Forward) — never overwrite it */ }
      else if (presetCity) {
        localStorage.setItem(SMART_INTAKE_KEY, JSON.stringify({
          categorySlug, categoryName, city: presetCity.name,
          cityLat: presetCity.lat, cityLng: presetCity.lng,
          cleanNeed: null, priceHint: null, timingHint: null, customNeed: null, sessionId: null,
        })); notifySmartIntake();
      } else {
        const saved = localStorage.getItem(SMART_INTAKE_KEY);
        const ctx = saved ? JSON.parse(saved) : null;
        if (!ctx || ctx.categorySlug !== categorySlug) {
          localStorage.removeItem(SMART_INTAKE_KEY);
        }
      }
    } catch {}
  }, [categorySlug, categoryName, presetCity?.name, presetCity?.lat, presetCity?.lng]);

  // Server already supplied the questionnaire (or explicitly null) — client fetch is a fallback.
  useEffect(() => {
    if (initialQuestionnaire !== undefined) return;
    fetch(`/api/questionnaire?category=${encodeURIComponent(categorySlug)}`)
      .then(r => r.json())
      .then(d => setQuestionnaire(d.questionnaire))
      .catch(() => setQuestionnaire(null));
  }, [categorySlug, initialQuestionnaire]);

  // Condense the instant the card's top edge reaches the very top of the viewport —
  // it docks at top:0 and covers the header from that point, so there's no gap to
  // reserve (unlike the old "pin under the header" behavior, which wanted a 58px margin).
  // The wrapper keeps its measured height while condensed so the page doesn't jump.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || noFloat) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        const shouldCondense = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        setCondensed(prev => {
          if (shouldCondense && !prev) fullHeight.current = el.offsetHeight;
          return shouldCondense;
        });
      },
      { rootMargin: '0px 0px 0px 0px', threshold: 0 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [questionnaire, noFloat]);

  if (!questionnaire && !pro) return null; // category page without a questionnaire → nothing (as classic)

  const attract = questionnaire && !started && !condensed ? 'quiz-attract' : '';

  return (
    <>
      <div id={pro ? 'pro-quiz' : 'cat-quiz'} dir="rtl" ref={wrapperRef}
        style={{ marginBottom: pro ? 24 : 20, scrollMarginTop: 70, minHeight: condensed ? fullHeight.current || undefined : undefined }}>
        {/* Positioning layer: in-flow normally; docks at the very top of the viewport
            (covering Header.tsx, z-50) while condensed — full-bleed brand gradient so it
            reads as "the header became the questionnaire," with extra vertical room now
            that it isn't sharing space with the 58px header above it (long questions can
            wrap to 2-3 lines instead of being squeezed onto one). */}
        <div style={condensed
          ? { position: 'fixed', top: 0, left: 0, right: 0, zIndex: 60, background: BRAND_GRADIENT, boxShadow: '0 4px 18px rgba(13,71,161,.3)' }
          : undefined}>
          <div className={condensed ? 'max-w-5xl mx-auto px-4' : undefined} style={condensed ? { padding: '10px 14px 12px' } : undefined}>
            <div className={attract} style={{
              borderRadius: condensed ? 14 : 18,
              border: condensed ? 'none' : '1.5px solid #e0f2fe',
              background: '#fff', overflow: 'hidden',
              boxShadow: condensed ? '0 2px 10px rgba(0,0,0,.15)' : '0 2px 16px rgba(13,71,161,.07)',
              transition: 'box-shadow .25s, border-radius .25s',
            }}>
              {attract && <span className="quiz-spark" />}
              {/* Full gradient banner — expanded (non-condensed) state only. Once condensed,
                  the OUTER positioning layer above already carries the same blue-forward
                  gradient full-bleed (replacing the header), so repeating it here would be
                  redundant. hideHeader (article embed) skips this banner in either state —
                  the article's own H2/prose already carries that context. */}
              {!condensed && !hideHeader && (
                <div style={{ background: BRAND_GRADIENT }}>
                  <div style={{ display: 'flex', minHeight: 60, color: '#fff' }}>
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '12px 12px' }}>
                      <span style={{ fontSize: 15.5, fontWeight: 900, lineHeight: 1.35 }}>✨ ספר לנו מה אתה צריך בקצרה</span>
                    </div>
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '12px 12px' }}>
                      <span style={{ fontSize: 15.5, fontWeight: 900, lineHeight: 1.35, display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                        <WaIcon size={18} /> {pro ? `ונכין הודעה לשליחה מהירה ל${previewName || 'מומחה'}` : 'ונכין הודעות לשליחה מהירה למומחים'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {questionnaire ? (
                <QuestionnaireWizard
                  questionnaire={questionnaire}
                  categoryName={categoryName}
                  proCity={pro?.city}
                  presetCity={presetCity}
                  skipCityQuestion={skipCityQuestion}
                  multi={!pro}
                  condensed={condensed}
                  onStart={() => setStarted(true)}
                />
              ) : questionnaire === null && pro && pro.phone ? (
                /* Pro page without a questionnaire → editable WA compose (as classic ProSmartIntake) */
                <div style={{ padding: 14 }}>
                  <EditableCompose pro={pro} categorySlug={categorySlug} categoryName={categoryName} />
                </div>
              ) : (
                <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>טוען...</div>
              )}

              {/* Live message preview inside the layout itself — condensed already has
                  CompactWaBar covering it at the bottom, no room to duplicate here. */}
              {questionnaire && !condensed && (
                <InlineWaPreview categoryName={categoryName} pro={pro} previewName={previewName} />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Live WhatsApp preview + send, pinned to the viewport bottom (mobile + desktop) */}
      {!noFloat && <CompactWaBar categorySlug={categorySlug} categoryName={categoryName} pro={pro} previewName={previewName} />}
    </>
  );
}

// Small stateful wrapper for the no-questionnaire pro fallback (editable bubble).
function EditableCompose({ pro, categorySlug, categoryName }: { pro: CompactBarPro; categorySlug: string; categoryName: string }) {
  const first = (pro.firstName && pro.firstName.trim()) || pro.name.split(/[\s\-,]+/)[0];
  const [text, setText] = useState(first ? `שלום ${first},\n` : '');
  return (
    <WaSmartButton proArea={null}
      proName={pro.name} proFirstName={pro.firstName || undefined} proPhone={pro.phone} proId={pro.id} proGender={pro.gender}
      categorySlug={categorySlug} categoryName={categoryName}
      editable value={text} onChange={setText}
    />
  );
}
